import type { RequestOptions } from '@@/plugin-request/request';
import type { RequestConfig } from '@umijs/max';
import React from 'react';
import { getAdminToken } from './utils/adminToken';
import { getAppApis } from './utils/antdApp';

// 错误处理方案： 错误类型
enum ErrorShowType {
  SILENT = 0,
  WARN_MESSAGE = 1,
  ERROR_MESSAGE = 2,
  NOTIFICATION = 3,
  REDIRECT = 9,
}
// 与后端约定的响应数据格式
interface ResponseStructure {
  success: boolean;
  data: any;
  errorCode?: number;
  errorMessage?: string;
  showType?: ErrorShowType;
}

type PagingInfo = Record<string, any>;

const toNumber = (value: unknown): number | undefined => {
  if (typeof value === 'number' && !Number.isNaN(value)) return value;
  if (typeof value === 'string' && value.trim()) {
    const parsed = Number(value);
    if (!Number.isNaN(parsed)) return parsed;
  }
  return undefined;
};

const normalizePagingInfo = (
  input?: PagingInfo,
):
  | (PagingInfo & { total?: number; page?: number; pageSize?: number })
  | undefined => {
  if (!input || typeof input !== 'object') return undefined;
  const normalized: PagingInfo & {
    total?: number;
    page?: number;
    pageSize?: number;
  } = {
    ...input,
  };

  const total = toNumber(
    input.total ??
      input.totalCount ??
      input.count ??
      input.total_items ??
      input.totalRecords ??
      input.totalElements ??
      input.totalHits,
  );
  if (total !== undefined) {
    normalized.total = total;
  }

  const page = toNumber(
    input.page ??
      input.current ??
      input.pageNo ??
      input.page_number ??
      input.pageIndex ??
      input.pageNum,
  );
  if (page !== undefined) {
    normalized.page = page;
    if (normalized.current === undefined) normalized.current = page;
  }

  const pageSize = toNumber(
    input.pageSize ??
      input.limit ??
      input.page_size ??
      input.perPage ??
      input.per_page ??
      input.size,
  );
  if (pageSize !== undefined) {
    normalized.pageSize = pageSize;
    if (normalized.limit === undefined) normalized.limit = pageSize;
    if (normalized.perPage === undefined) normalized.perPage = pageSize;
  }

  return normalized;
};

const pickPageStats = (info?: {
  total?: number;
  page?: number;
  pageSize?: number;
}) => {
  const result: Record<string, number> = {};
  if (typeof info?.total === 'number') result.total = info.total;
  if (typeof info?.page === 'number') result.page = info.page;
  if (typeof info?.pageSize === 'number') result.pageSize = info.pageSize;
  return result;
};

const isPlainObject = (value: unknown): value is Record<string, any> => {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
};

/**
 * @name 错误处理
 * pro 自带的错误处理， 可以在这里做自己的改动
 * @doc https://umijs.org/docs/max/request#配置
 */
export const errorConfig: RequestConfig = {
  // 错误处理： umi@3 的错误处理方案。
  errorConfig: {
    // 错误抛出
    errorThrower: (res) => {
      const { success, data, errorCode, errorMessage, showType } =
        res as unknown as ResponseStructure;
      if (success === false) {
        const error: any = new Error(errorMessage);
        error.name = 'BizError';
        error.info = { errorCode, errorMessage, showType, data };
        throw error; // 抛出自制的错误
      }
    },
    // 错误接收及处理
    errorHandler: (error: any, opts: any) => {
      const { message, notification } = getAppApis();
      if (opts?.skipErrorHandler) throw error;
      // 我们的 errorThrower 抛出的错误。
      if (error.name === 'BizError') {
        const errorInfo: any = error.info;
        if (errorInfo) {
          const errMsg: string =
            errorInfo.errorMessage ||
            errorInfo.message ||
            error.message ||
            '请求失败';
          const code = errorInfo.errorCode || errorInfo.code;
          const correlationId = errorInfo.correlationId || errorInfo.traceId;
          const path = errorInfo.path;
          const ts = errorInfo.timestamp;
          const status = errorInfo.status || error.response?.status;
          const title = code
            ? `${code} (${status || ''})`
            : `请求失败 (${status || ''})`;

          // 将原始错误对象的 message 替换为后端 message，便于上层捕获展示
          try {
            (error as any).message = errMsg;
          } catch {}

          switch (errorInfo.showType) {
            case ErrorShowType.SILENT:
              break;
            case ErrorShowType.WARN_MESSAGE:
              message.warning(errMsg);
              break;
            case ErrorShowType.NOTIFICATION:
              notification.error({
                message: title,
                description: React.createElement(
                  'div',
                  { style: { whiteSpace: 'pre-wrap' } },
                  [
                    errMsg,
                    correlationId || path || ts
                      ? `\n${[
                          correlationId ? `请求ID: ${correlationId}` : null,
                          path ? `路径: ${path}` : null,
                          ts ? `时间: ${ts}` : null,
                        ]
                          .filter(Boolean)
                          .join(' | ')}`
                      : '',
                  ]
                    .filter(Boolean)
                    .join(''),
                ),
                duration: 6,
              });
              break;
            case ErrorShowType.REDIRECT:
              break;
            default:
              message.error(errMsg);
          }
        }
      } else if (error.response) {
        // Axios 的错误
        // 请求成功发出且服务器也响应了状态码，但状态代码超出了 2xx 的范围
        const payload = error.response?.data;
        if (
          payload &&
          typeof payload === 'object' &&
          ('message' in payload || 'errorMessage' in payload)
        ) {
          const errMsg = (payload.message || payload.errorMessage) as string;
          const code = (payload.errorCode || payload.code) as any;
          const correlationId = (payload.correlationId || payload.traceId) as
            | string
            | undefined;
          const path = (payload.path as string) || undefined;
          const ts = (payload.timestamp as string) || undefined;
          notification.error({
            message: code
              ? `${code} (${error.response.status})`
              : `请求失败 (${error.response.status})`,
            description: React.createElement(
              'div',
              { style: { whiteSpace: 'pre-wrap' } },
              [
                errMsg,
                correlationId || path || ts
                  ? `\n${[
                      correlationId ? `请求ID: ${correlationId}` : null,
                      path ? `路径: ${path}` : null,
                      ts ? `时间: ${ts}` : null,
                    ]
                      .filter(Boolean)
                      .join(' | ')}`
                  : '',
              ]
                .filter(Boolean)
                .join(''),
            ),
            duration: 6,
          });
          try {
            (error as any).message = errMsg;
          } catch {}
        } else {
          message.error(`Response status:${error.response.status}`);
        }
      } else if (error.request) {
        // 请求已经成功发起，但没有收到响应
        // \`error.request\` 在浏览器中是 XMLHttpRequest 的实例，
        // 而在node.js中是 http.ClientRequest 的实例
        message.error('None response! Please retry.');
      } else {
        // 发送请求时出了点问题
        message.error('Request error, please retry.');
      }
    },
  },

  // 请求拦截器
  requestInterceptors: [
    (config: RequestOptions) => {
      const url = config?.url || '';
      const needsAdminToken =
        url.includes('/api/admin/') ||
        url.includes('/api/customizations/test-order/') ||
        /\/api\/customizations\/products\/.+\/(validation|publish)/.test(url);
      if (needsAdminToken) {
        const adminToken = getAdminToken();
        if (adminToken) {
          config.headers = {
            ...config.headers,
            Authorization: `Bearer ${adminToken}`,
          };
        }
      }
      return config;
    },
  ],

  // 响应拦截器
  responseInterceptors: [
    (response) => {
      const mutableResponse = response as any;
      // 对于admin API调用，自动解包数据
      if (
        mutableResponse.config?.url?.includes('/api/admin/')
      ) {
        const responseData = mutableResponse.data;

        // 如果响应包含 success 字段，按约定处理成功/失败
        if (
          responseData &&
          typeof responseData === 'object' &&
          'success' in responseData
        ) {
          if ((responseData as any).success) {
            const rawPagination = (responseData as any).pagination;
            const rawMeta = (responseData as any).meta;
            const normalizedPagination = normalizePagingInfo(rawPagination);
            const normalizedMeta = normalizePagingInfo(rawMeta);
            // 兼容后端直接把 total/page/pageSize 挂在最外层（如订单列表），
            // 但仅在确实存在这些字段时启用，避免详情接口被错误包装成 pagination 结构。
            const rootPagingInfo = pickPageStats(responseData as any);
            const normalizedRootPaging =
              Object.keys(rootPagingInfo).length > 0
                ? normalizePagingInfo(responseData as any)
                : undefined;
            const payloadData =
              'data' in (responseData as any)
                ? (responseData as any).data
                : responseData;
            const payloadIsListContainer =
              isPlainObject(payloadData) && Array.isArray(payloadData.data);
            let structuredListResult: Record<string, any> | null = null;

            if (payloadIsListContainer) {
              const {
                data: listData,
                pagination: innerPagination,
                meta: innerMeta,
                ...rest
              } = payloadData as any;
              const normalizedInnerPagination =
                normalizePagingInfo(innerPagination) ||
                normalizePagingInfo(innerMeta) ||
                normalizePagingInfo(rest);
              const effectivePagination =
                normalizedPagination ||
                normalizedMeta ||
                normalizedRootPaging ||
                normalizedInnerPagination;
              const pagingFields = pickPageStats(effectivePagination);
              structuredListResult = {
                data: listData,
                ...rest,
                ...(innerMeta ? { meta: innerMeta } : {}),
                ...(effectivePagination
                  ? { pagination: effectivePagination }
                  : {}),
                ...(Object.keys(pagingFields).length ? pagingFields : {}),
              };
            }
            const messageField = (responseData as any).message;

            if (structuredListResult) {
              mutableResponse.data = {
                ...structuredListResult,
                ...(messageField ? { message: messageField } : {}),
              };
            } else if (
              normalizedPagination ||
              normalizedMeta ||
              normalizedRootPaging
            ) {
              const pagingInfo =
                normalizedPagination || normalizedMeta || normalizedRootPaging;
              const pagingFields = pickPageStats(pagingInfo);
              mutableResponse.data = {
                data: payloadData,
                ...(normalizedMeta ? { meta: normalizedMeta } : {}),
                ...(pagingInfo ? { pagination: pagingInfo } : {}),
                ...(Object.keys(pagingFields).length ? pagingFields : {}),
                ...(messageField ? { message: messageField } : {}),
              };
            } else if (messageField && isPlainObject(payloadData)) {
              mutableResponse.data = {
                ...payloadData,
                message: messageField,
              };
            } else if (messageField) {
              mutableResponse.data = {
                data: payloadData,
                message: messageField,
              };
            } else {
              mutableResponse.data = payloadData;
            }
          } else {
            const error: any = new Error(
              (responseData as any).message ||
                (responseData as any).errorMessage ||
                '请求失败',
            );
            error.name = 'BizError';
            error.info = {
              errorCode: (responseData as any).errorCode,
              errorMessage:
                (responseData as any).message ||
                (responseData as any).errorMessage ||
                '请求失败',
              showType: ErrorShowType.NOTIFICATION,
              data: (responseData as any).data,
              correlationId: (responseData as any).correlationId,
              timestamp: (responseData as any).timestamp,
              path: (responseData as any).path,
              status: (response as any)?.status,
              fromAdminApi: true,
            };
            throw error;
          }
        }
      } else {
        // 对于非admin API，保持原有逻辑
        const { data } = response as unknown as ResponseStructure;
        if (data?.success === false) {
          const { message } = getAppApis();
          message.error('请求失败！');
        }
      }

      return response;
    },
  ],
};
